import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, Info, Sparkles, ArrowRight, Play } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Card, CardEyebrow, CardTitle, CardBody } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Tag } from "@/components/ui/Tag";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Select } from "@/components/ui/Select";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@/components/ui/Tabs";
import { Switch } from "@/components/ui/Switch";
import { Dialog, DialogFooter } from "@/components/ui/Dialog";
import { Tooltip } from "@/components/ui/Tooltip";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";

import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Counter } from "@/components/motion/Counter";

const BUTTON_VARIANTS_LIGHT = ["primary", "secondary", "ghost", "dark"];
const BUTTON_VARIANTS_DARK = ["primary-on-dark", "secondary-on-dark"];
const SIZES = ["sm", "md", "lg"];
const SEMANTIC_BADGES = [
  "primary",
  "violet",
  "neutral",
  "success",
  "warning",
  "danger",
];
const STATUS_BADGES = ["LIVE", "IN_PARTNERSHIP", "PILOT", "ROADMAP"];

export default function KitchenSink() {
  return (
    <div className="min-h-screen" data-testid="kitchen-sink">
      <Helmet>
        <title>Kitchen sink — Frac (internal)</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* -------- Dark surface half -------- */}
      <div className="bg-surface-base text-ink-primary">
        <div className="container-frac py-16">
          <div className="mb-12 flex items-center justify-between">
            <div>
              <span className="eyebrow">INTERNAL · DESIGN SYSTEM</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Kitchen sink — dark surface
              </h1>
              <p className="mt-3 max-w-2xl text-lg text-ink-secondary">
                Every component × every variant × every size. Not in the sitemap.
                Not linked from anywhere. Existence is deliberate.
              </p>
            </div>
            <Badge variant="violet" onDark>
              /_kitchen-sink
            </Badge>
          </div>

          <Section title="Buttons — light-surface variants (rendered on dark for tester convenience)">
            <VariantGrid>
              {BUTTON_VARIANTS_LIGHT.map((v) => (
                <div key={v} className="grid gap-3">
                  <Label>{v}</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    {SIZES.map((s) => (
                      <Button
                        key={s}
                        variant={v}
                        size={s}
                        data-testid={`kitchen-btn-${v}-${s}`}
                      >
                        Button {s}
                      </Button>
                    ))}
                    <Button variant={v} size="md" disabled>
                      Disabled
                    </Button>
                    <Button variant={v} size="md" loading>
                      Loading
                    </Button>
                  </div>
                </div>
              ))}
            </VariantGrid>
          </Section>

          <Section title="Buttons — dark-surface variants">
            <VariantGrid>
              {BUTTON_VARIANTS_DARK.map((v) => (
                <div key={v} className="grid gap-3">
                  <Label>{v}</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    {SIZES.map((s) => (
                      <Button
                        key={s}
                        variant={v}
                        size={s}
                        data-testid={`kitchen-btn-${v}-${s}`}
                      >
                        Button {s} <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                      </Button>
                    ))}
                    <Button variant={v} size="md" disabled>
                      Disabled
                    </Button>
                    <Button variant={v} size="md" loading>
                      Loading
                    </Button>
                  </div>
                </div>
              ))}
              <div className="grid gap-3">
                <Label>As router link</Label>
                <div className="flex gap-3">
                  <Button
                    as="link"
                    to="/contact"
                    variant="primary-on-dark"
                    size="md"
                  >
                    Book a Demo
                  </Button>
                  <Button as="a" href="mailto:hello@frac.io" variant="secondary-on-dark">
                    Email us
                  </Button>
                </div>
              </div>
            </VariantGrid>
          </Section>

          <Section title="Icon buttons">
            <div className="flex flex-wrap items-center gap-3">
              {[...BUTTON_VARIANTS_LIGHT, ...BUTTON_VARIANTS_DARK].map((v) =>
                SIZES.map((s) => (
                  <IconButton
                    key={`${v}-${s}`}
                    variant={v}
                    size={s}
                    icon={Copy}
                    aria-label={`Copy (${v} ${s})`}
                    data-testid={`kitchen-iconbtn-${v}-${s}`}
                  />
                )),
              )}
            </div>
          </Section>

          <Section title="Cards">
            <div className="grid gap-6 lg:grid-cols-3">
              <Card variant="glass" interactive>
                <CardEyebrow>GLASS · INTERACTIVE</CardEyebrow>
                <CardTitle>Signal in. Structure out.</CardTitle>
                <CardBody>
                  Hover to see the border-shift and lift. Primary variant used
                  on all dark-surface pages.
                </CardBody>
              </Card>
              <Card variant="dark">
                <CardEyebrow>DARK</CardEyebrow>
                <CardTitle>Solid dark surface</CardTitle>
                <CardBody>Solid #02040B tile with hairline border.</CardBody>
              </Card>
              <Card variant="gradient">
                <CardEyebrow className="text-frac-white/70">GRADIENT</CardEyebrow>
                <CardTitle className="text-frac-white">
                  Brand gradient card
                </CardTitle>
                <CardBody className="text-frac-white/85">
                  Uses --gradient-brand as the fill.
                </CardBody>
              </Card>
            </div>
          </Section>

          <Section title="Badges — semantic (on-dark)">
            <div className="flex flex-wrap gap-3">
              {SEMANTIC_BADGES.map((v) => (
                <Badge key={v} variant={v} onDark>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </Badge>
              ))}
            </div>
          </Section>

          <Section title="Badges — capability status">
            <div className="flex flex-wrap gap-3">
              {STATUS_BADGES.map((s) => (
                <Badge key={s} status={s} onDark />
              ))}
            </div>
          </Section>

          <Section title="Tags">
            <TagsDemo onDark />
          </Section>

          <Section title="Inputs · Textarea · Select (on-dark)">
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="Full name"
                placeholder="Ada Lovelace"
                hint="Two characters or more."
                onDark
              />
              <Input
                label="Work email"
                type="email"
                placeholder="you@company.com"
                error="Please use a work email."
                onDark
              />
              <Select
                label="Area of interest"
                placeholder="Select one…"
                onDark
                options={[
                  { value: "marketing_technology", label: "Marketing Technology" },
                  { value: "ai_infrastructure", label: "AI Infrastructure" },
                  { value: "data_tokenization", label: "Data Tokenization" },
                ]}
              />
              <Textarea
                label="Message"
                placeholder="Tell us what you're building."
                hint="10–5000 characters."
                onDark
              />
            </div>
          </Section>

          <Section title="Tabs (on-dark)">
            <TabsDemo onDark />
          </Section>

          <Section title="Switch">
            <div className="flex flex-wrap items-center gap-8">
              <SwitchDemo onDark />
              <SwitchDemo onDark disabled />
            </div>
          </Section>

          <Section title="Dialog · Tooltip · Toast">
            <div className="flex flex-wrap items-center gap-4">
              <DialogDemo onDark />
              <Tooltip content="Copy the link to this section">
                <Button variant="secondary-on-dark">Hover for tooltip</Button>
              </Tooltip>
              <ToastDemo />
            </div>
          </Section>

          <Section title="Spinner (with reduced-motion static dots)">
            <div className="flex items-center gap-6">
              <span className="text-ink-primary">
                <Spinner size="sm" />
              </span>
              <span className="text-ink-primary">
                <Spinner size="md" />
              </span>
              <span className="text-ink-primary">
                <Spinner size="lg" />
              </span>
            </div>
          </Section>

          <Section title="Motion primitives">
            <MotionDemo />
          </Section>
        </div>
      </div>

      {/* -------- Light surface half -------- */}
      <div className="bg-frac-white text-frac-black">
        <div className="container-frac py-16">
          <div className="mb-12">
            <span className="eyebrow" style={{ color: "var(--frac-violet)" }}>
              INTERNAL · DESIGN SYSTEM
            </span>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-frac-black sm:text-5xl">
              Kitchen sink — light surface
            </h2>
            <p className="mt-3 max-w-2xl text-lg text-frac-gray-70">
              Same components rendered against #EDF1F5.
            </p>
          </div>

          <Section title="Buttons — light-surface variants" light>
            <VariantGrid light>
              {BUTTON_VARIANTS_LIGHT.map((v) => (
                <div key={v} className="grid gap-3">
                  <Label light>{v}</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    {SIZES.map((s) => (
                      <Button key={s} variant={v} size={s}>
                        Button {s}
                      </Button>
                    ))}
                    <Button variant={v} size="md" disabled>
                      Disabled
                    </Button>
                    <Button variant={v} size="md" loading>
                      Loading
                    </Button>
                  </div>
                </div>
              ))}
            </VariantGrid>
          </Section>

          <Section title="Cards — light" light>
            <div className="grid gap-6 lg:grid-cols-3">
              <Card variant="default">
                <CardEyebrow>DEFAULT</CardEyebrow>
                <CardTitle className="text-frac-black">Default card</CardTitle>
                <CardBody className="text-frac-gray-70">
                  White background, subtle border, elev-sm.
                </CardBody>
              </Card>
              <Card variant="outlined">
                <CardEyebrow>OUTLINED</CardEyebrow>
                <CardTitle className="text-frac-black">Outlined card</CardTitle>
                <CardBody className="text-frac-gray-70">
                  Transparent, 1px gray-30 border.
                </CardBody>
              </Card>
              <Card variant="gradient">
                <CardEyebrow className="text-frac-white/80">GRADIENT</CardEyebrow>
                <CardTitle className="text-frac-white">Brand gradient</CardTitle>
                <CardBody className="text-frac-white/85">
                  Works on either surface.
                </CardBody>
              </Card>
            </div>
          </Section>

          <Section title="Badges — semantic (light)" light>
            <div className="flex flex-wrap gap-3">
              {SEMANTIC_BADGES.map((v) => (
                <Badge key={v} variant={v}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </Badge>
              ))}
            </div>
          </Section>

          <Section title="Badges — capability status (light)" light>
            <div className="flex flex-wrap gap-3">
              {STATUS_BADGES.map((s) => (
                <Badge key={s} status={s} />
              ))}
            </div>
          </Section>

          <Section title="Tags — light" light>
            <TagsDemo />
          </Section>

          <Section title="Inputs · Textarea · Select — light" light>
            <div className="grid gap-6 md:grid-cols-2">
              <Input label="Full name" placeholder="Ada Lovelace" hint="Two characters or more." />
              <Input label="Work email" type="email" placeholder="you@company.com" error="Please use a work email." />
              <Select
                label="Area of interest"
                placeholder="Select one…"
                options={[
                  { value: "marketing_technology", label: "Marketing Technology" },
                  { value: "partnership", label: "Partnership" },
                ]}
              />
              <Textarea label="Message" placeholder="Tell us what you're building." hint="10–5000 characters." />
            </div>
          </Section>

          <Section title="Tabs — light" light>
            <TabsDemo />
          </Section>

          <Section title="Switch — light" light>
            <div className="flex flex-wrap items-center gap-8">
              <SwitchDemo />
              <SwitchDemo disabled />
            </div>
          </Section>

          <Section title="Dialog · Tooltip — light" light>
            <div className="flex flex-wrap items-center gap-4">
              <DialogDemo />
              <Tooltip content="Runs on the light surface too">
                <Button variant="secondary">Hover for tooltip</Button>
              </Tooltip>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

/* -------- helpers -------- */

function Section({ title, light, children }) {
  return (
    <section className="mb-14">
      <h3
        className={
          "text-xs font-semibold uppercase tracking-caps " +
          (light ? "text-frac-gray-70" : "text-ink-tertiary")
        }
      >
        {title}
      </h3>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function VariantGrid({ children }) {
  return <div className="grid gap-8">{children}</div>;
}

function Label({ children, light }) {
  return (
    <div
      className={
        "text-[11px] font-semibold uppercase tracking-caps " +
        (light ? "text-frac-gray-50" : "text-ink-tertiary")
      }
    >
      {children}
    </div>
  );
}

function TagsDemo({ onDark = false }) {
  const [selected, setSelected] = useState("data");
  const items = [
    { id: "marketing", label: "Marketing" },
    { id: "ai", label: "AI" },
    { id: "data", label: "Data tokenization" },
    { id: "energy", label: "Energy" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t) => (
        <Tag
          key={t.id}
          selected={selected === t.id}
          onClick={() => setSelected(t.id)}
          onDark={onDark}
        >
          {t.label}
        </Tag>
      ))}
    </div>
  );
}

function TabsDemo({ onDark = false }) {
  const [tab, setTab] = useState("one");
  return (
    <Tabs value={tab} onValueChange={setTab} onDark={onDark}>
      <TabsList aria-label="Kitchen sink tabs demo">
        <TabsTrigger value="one">Overview</TabsTrigger>
        <TabsTrigger value="two">Metering</TabsTrigger>
        <TabsTrigger value="three">Licensing</TabsTrigger>
      </TabsList>
      <TabsPanel value="one" className="mt-6 text-sm">
        First panel. Arrow keys move focus between tabs.
      </TabsPanel>
      <TabsPanel value="two" className="mt-6 text-sm">
        Second panel content.
      </TabsPanel>
      <TabsPanel value="three" className="mt-6 text-sm">
        Third panel content.
      </TabsPanel>
    </Tabs>
  );
}

function SwitchDemo({ onDark = false, disabled = false }) {
  const [on, setOn] = useState(false);
  return (
    <Switch
      checked={on}
      onCheckedChange={setOn}
      onDark={onDark}
      disabled={disabled}
      label={disabled ? "Disabled" : on ? "Notifications on" : "Notifications off"}
    />
  );
}

function DialogDemo({ onDark = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant={onDark ? "secondary-on-dark" : "secondary"}
        onClick={() => setOpen(true)}
      >
        Open dialog
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        onDark={onDark}
        title="Confirm demo action"
        description="This dialog demonstrates the primitive. Escape or click backdrop to close."
      >
        <p className={onDark ? "text-ink-secondary text-sm" : "text-frac-gray-70 text-sm"}>
          Focus is trapped inside the panel. The trigger regains focus on close.
        </p>
        <DialogFooter>
          <Button variant={onDark ? "secondary-on-dark" : "secondary"} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant={onDark ? "primary-on-dark" : "primary"} onClick={() => setOpen(false)}>
            Confirm
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}

function ToastDemo() {
  const toast = useToast();
  return (
    <div className="flex flex-wrap gap-3">
      <Button variant="secondary-on-dark" onClick={() => toast.show("Default toast")}>
        Default
      </Button>
      <Button
        variant="secondary-on-dark"
        onClick={() => toast.success("Subscribed. We'll be in touch.")}
      >
        Success
      </Button>
      <Button
        variant="secondary-on-dark"
        onClick={() => toast.warning("Almost at the rate limit.")}
      >
        Warning
      </Button>
      <Button
        variant="secondary-on-dark"
        onClick={() => toast.danger("Something went wrong.")}
      >
        Danger
      </Button>
    </div>
  );
}

function MotionDemo() {
  const [replayKey, setReplayKey] = useState(0);
  const stats = useMemo(
    () => [
      { label: "Signals ingested", to: 42_650_000, format: (n) => Math.round(n / 1_000_000) + "M" },
      { label: "Deployments live", to: 128 },
      { label: "APAC enterprise partners", to: 24 },
    ],
    [],
  );
  return (
    <div key={replayKey} className="grid gap-8">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary-on-dark"
          size="sm"
          onClick={() => setReplayKey((k) => k + 1)}
          data-testid="kitchen-motion-replay"
        >
          <Play className="h-4 w-4" strokeWidth={1.75} /> Replay animations
        </Button>
        <span className="text-xs text-ink-tertiary">
          Respects prefers-reduced-motion — under that setting, all four
          primitives render immediately.
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card variant="glass">
          <CardEyebrow>REVEAL</CardEyebrow>
          <Reveal delay={0} className="mt-4">
            <p className="text-ink-primary">
              Reveal — fades and slides up 24px on first intersection.
            </p>
          </Reveal>
        </Card>

        <Card variant="glass">
          <CardEyebrow>STAGGER</CardEyebrow>
          <Stagger gap={90} className="mt-4 grid gap-2">
            <StaggerItem>
              <p className="text-ink-primary">One unit.</p>
            </StaggerItem>
            <StaggerItem>
              <p className="text-ink-primary">One owner.</p>
            </StaggerItem>
            <StaggerItem>
              <p className="text-ink-primary">One count.</p>
            </StaggerItem>
          </Stagger>
        </Card>

        <Card variant="glass">
          <CardEyebrow>MASK REVEAL</CardEyebrow>
          <MaskReveal
            as="h4"
            className="mt-4 text-2xl font-bold leading-tight tracking-tight text-ink-primary"
            lines={["Automate the marketing.", "Keep the secrets."]}
          />
        </Card>

        <Card variant="glass">
          <CardEyebrow>COUNTERS</CardEyebrow>
          <div className="mt-4 grid grid-cols-3 gap-6">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-light tracking-tight text-frac-violet">
                  <Counter to={s.to} format={s.format} />
                </div>
                <div className="mt-1 text-xs text-ink-tertiary">{s.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
